import { useState } from "react";
import { Outlet, useLocation, NavLink, Link } from "react-router-dom";
import styled from "styled-components";
import Footer from "./components/Footer";
import { MelodyLogo } from "./components/MelogyLogo";

// ── Constants ────────────────────────────────────────────────────────────────

const NAV_HEIGHT = 64;
const FOOTER_HEIGHT = 60;

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Research", to: "https://www.nmzlab.pitt.edu/research" },
  { label: "Team", to: "https://www.nmzlab.pitt.edu/people" },
  { label: "Contact", to: "https://www.nmzlab.pitt.edu/contact-us" },
];

const bp = {
  mobile: "700px",
};

// ── Styled Components ────────────────────────────────────────────────────────

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  padding: 5px 10px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-right: 12px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background-color: #f0f2f7;
`;

// Desktop: 3-column grid — unchanged
// Mobile: 2-column (logo | hamburger), title hidden
const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${NAV_HEIGHT}px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  background-color: #0a2260;
  padding: 0 1.5rem;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;

  @media (max-width: ${bp.mobile}) {
    grid-template-columns: 1fr auto;
    padding: 0 1rem;
  }
`;

const NavLogo = styled.div`
  display: flex;
  align-items: center;

  img {
    height: 42px;
    width: auto;
  }
`;

// Hidden on mobile — BOHÈME only shows on desktop
const NavTitle = styled.h1`
  margin: 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  white-space: nowrap;
  text-align: center;

  @media (max-width: ${bp.mobile}) {
    display: none;
  }
`;

// Desktop nav links — hidden on mobile
const NavLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.1rem;

  @media (max-width: ${bp.mobile}) {
    display: none;
  }
`;

const NavItem = styled(NavLink)`
  font-family: Arial, Helvetica, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  padding: 5px 10px;
  border-radius: 3px;
  transition: background 0.15s, color 0.15s;

  &:hover,
  &.active {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }
`;

// Hamburger button — only visible on mobile
const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  color: white;
  flex-direction: column;
  justify-content: center;
  gap: 5px;

  @media (max-width: ${bp.mobile}) {
    display: flex;
  }
`;

const HamburgerLine = styled.span`
  display: block;
  width: 22px;
  height: 2px;
  background: white;
  border-radius: 2px;
  transition: transform 0.2s, opacity 0.2s;

  &:nth-child(1) {
    transform: ${({ $open }) =>
      $open ? "translateY(7px) rotate(45deg)" : "none"};
  }
  &:nth-child(2) {
    opacity: ${({ $open }) => ($open ? 0 : 1)};
  }
  &:nth-child(3) {
    transform: ${({ $open }) =>
      $open ? "translateY(-7px) rotate(-45deg)" : "none"};
  }
`;

// Mobile dropdown menu
const MobileMenu = styled.div`
  display: none;

  @media (max-width: ${bp.mobile}) {
    display: ${({ $open }) => ($open ? "flex" : "none")};
    flex-direction: column;
    position: fixed;
    top: ${NAV_HEIGHT}px;
    left: 0;
    right: 0;
    background: #0a2260;
    z-index: 999;
    padding: 0.5rem 0 1rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const MobileNavItem = styled(NavLink)`
  font-family: Arial, Helvetica, sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  transition: background 0.15s, color 0.15s;

  &:hover,
  &.active {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const Content = styled.main`
  flex: 1;
  overflow-y: auto;
  padding-top: ${NAV_HEIGHT}px;
  padding-bottom: ${({ $hasFooter }) =>
    $hasFooter ? `${FOOTER_HEIGHT}px` : 0};

  @media (max-width: 600px) {
    padding-bottom: ${({ $hasFooter }) => ($hasFooter ? "90px" : 0)};
  }
`;

const FooterWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${FOOTER_HEIGHT}px;
  z-index: 1000;

  @media (max-width: 600px) {
    height: auto;
  }
`;

// ── Component ────────────────────────────────────────────────────────────────

export default function Layout() {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isHome = pathname === "/";
  const isViolin = pathname === "/violin";
  const isBell = pathname === "/bell";
  const isDish = pathname === "/dish";
  const isCelesta = pathname === "/celesta";
  const isNL2BLTL = pathname === "/nl2bltl";
  const isBioRecipe = pathname === "/biorecipe";

  const closeMenu = () => setMenuOpen(false);

  return (
    <Container>
      <Nav>
        {/* ── Left: logo + tool label ── */}
        <NavLogo>
          {!isHome && <BackButton to="/" onClick={closeMenu}>← Back</BackButton>}
          <Link
            to="/"
            onClick={closeMenu}
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "white",
            }}
          >
            {<MelodyLogo />}
            <div style={{ margin: "0 5px 0 10px" }}>MeLoDy Lab</div>
          </Link>
          {isViolin && (
            <>
              <div>| </div>
              <div style={{ color: "#FFB81C", marginLeft: "5px" }}>VIOLIN</div>
            </>
          )}
          {isBell && (
            <>
              <div>| </div>
              <div style={{ color: "#FFB81C", marginLeft: "5px" }}>BELL</div>
            </>
          )}
          {isDish && (
            <>
              <div>| </div>
              <div style={{ color: "#FFB81C", marginLeft: "5px" }}>DISH</div>
            </>
          )}
          {isCelesta && (
            <>
              <div>| </div>
              <div style={{ color: "#FFB81C", marginLeft: "5px" }}>CELESTA</div>
            </>
          )}
          {isNL2BLTL && (
            <>
              <div>| </div>
              <div style={{ color: "#FFB81C", marginLeft: "5px" }}>NL2BLTL</div>
            </>
          )}
          {isBioRecipe && (
            <>
              <div>| </div>
              <div style={{ color: "#FFB81C", marginLeft: "5px" }}>BioRECIPE</div>
            </>
          )}
        </NavLogo>

        {/* ── Center: BOHÈME — desktop only ── */}
        <NavTitle>BOHÈME</NavTitle>

        {/* ── Right: desktop nav links OR hamburger ── */}
        <NavLinks>
          {NAV_ITEMS.map(({ label, to }) => (
            <NavItem
              key={to}
              to={to}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              {label}
            </NavItem>
          ))}
        </NavLinks>

        <HamburgerButton
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle navigation menu"
        >
          <HamburgerLine $open={menuOpen} />
          <HamburgerLine $open={menuOpen} />
          <HamburgerLine $open={menuOpen} />
        </HamburgerButton>
      </Nav>

      {/* ── Mobile dropdown ── */}
      <MobileMenu $open={menuOpen}>
        {NAV_ITEMS.map(({ label, to }) => (
          <MobileNavItem
            key={to}
            to={to}
            onClick={closeMenu}
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            {label}
          </MobileNavItem>
        ))}
      </MobileMenu>

      <Content $hasFooter={isHome}>
        <Outlet />
      </Content>

      {isHome && (
        <FooterWrapper>
          <Footer isHomepage />
        </FooterWrapper>
      )}
    </Container>
  );
}
